import { Component } from "react";

/**
 * Minimal error boundary. Catches thrown render errors so a transient
 * navigation glitch never flashes the CRA/webpack error overlay to the
 * user. Logs to console for developer visibility; presents a small,
 * safe fallback card that lets the user retry or go home.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.warn("[ErrorBoundary] caught render error", error, info?.componentStack);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-[40vh] flex items-center justify-center px-6 py-10">
        <div
          className="jc-card p-6 max-w-md text-center"
          data-testid="app-error-boundary-fallback"
        >
          <div className="overline text-[var(--jc-text-soft)] mb-2">Something went wrong</div>
          <div className="font-display text-xl mb-2">We couldn&apos;t render that page.</div>
          <p className="text-sm text-[var(--jc-text-soft)] mb-4">
            Try again, or head back to your dashboard. Your data is safe.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={this.reset}
              data-testid="app-error-retry-btn"
              className="px-4 py-2 rounded-md text-sm font-semibold jc-btn-primary"
            >
              Try again
            </button>
            <a
              href="/app"
              data-testid="app-error-home-link"
              className="px-4 py-2 rounded-md text-sm font-semibold border border-[var(--jc-border-strong)]"
            >
              Back to dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
